import React from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import Logo from "../images/hotel_logo.png";
import { HOTEL_NAME } from "../Variables.js";
import { useUserAuth } from "../context/UserAuthContext";

const SalesPDFGenerator = ({ sales, days, buttonText, month }) => {
  const { user, userData } = useUserAuth();
  const hotelName = `${HOTEL_NAME}`;

  // Debug logging
  React.useEffect(() => {
    if (sales && sales.length > 0) {      
      sales.slice(0, 3).forEach((sale, i) => {
        const saleDate = convertFirestoreTimestamp(sale.timestamp);        
      });
    }
  }, [sales, days, month, buttonText]);

  const formatDate = (date) => {
    if (!date) return "N/A";
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (date) => {
    if (!date) return "N/A";
    return date
      .toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .replace(",", "");
  };

  const formatAmount = (amount) => {
    if (!amount) amount = 0;
    return `N${amount.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  };

  const getPeriodLabel = () => {
    if (buttonText && buttonText.includes("All Sales")) {
      return "All Sales";
    }
    
    if (month) {
      const [year, monthNum] = month.split("-");
      const monthNames = ["January", "February", "March", "April", "May", "June",
                         "July", "August", "September", "October", "November", "December"];
      return `${monthNames[parseInt(monthNum) - 1]} ${year}`;
    }
    
    if (days) {
      if (days === 1) return "Last 24 Hours";
      if (days === 7) return "Last 7 Days";
      if (days === 30) return "Last 30 Days";
      if (days === 90) return "Last 90 Days";
      return `Last ${days} Days`;
    }
    
    if (buttonText.startsWith("Print ")) {
      return buttonText.replace("Print ", "").replace(" Report", "");
    }
    
    return buttonText || "Sales Report";
  };

  const convertFirestoreTimestamp = (timestamp) => {
    if (!timestamp) return null;
    
    try {
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        return timestamp.toDate();
      }
      
      if (timestamp._seconds !== undefined) {
        return new Date(timestamp._seconds * 1000);
      }
      
      if (timestamp.seconds !== undefined) {
        return new Date(timestamp.seconds * 1000);
      }
      
      if (timestamp instanceof Date) {
        return timestamp;
      }
      
      if (typeof timestamp === 'string' || typeof timestamp === 'number') {
        return new Date(timestamp);
      }
      
      return null;
    } catch (error) {
      return null;
    }
  };

  const getFilteredSales = () => {
    if (!sales || sales.length === 0) {
      return [];
    }

    if (buttonText && (buttonText.includes("All Sales") || buttonText.includes("All Time"))) {
      return [...sales];
    }

    let filteredSales = [];
    
    if (month) {
      const [year, monthIndex] = month.split("-").map(Number);
      const startDate = new Date(year, monthIndex - 1, 1, 0, 0, 0, 0);
      const endDate = new Date(year, monthIndex, 0, 23, 59, 59, 999);    
      
      filteredSales = sales.filter((sale) => {
        if (!sale || !sale.timestamp) return false;
        
        const saleDate = convertFirestoreTimestamp(sale.timestamp);
        if (!saleDate) return false;
        
        return saleDate >= startDate && saleDate <= endDate;
      });  
    } else if (days) {
      const referenceDate = new Date();
      const cutoffDate = new Date(referenceDate);
      cutoffDate.setDate(cutoffDate.getDate() - days);
      cutoffDate.setHours(0, 0, 0, 0);
      
      const today = new Date(referenceDate);
      today.setHours(23, 59, 59, 999);      
      
      filteredSales = sales.filter((sale) => {
        if (!sale || !sale.timestamp) return false;
        const saleDate = convertFirestoreTimestamp(sale.timestamp);
        if (!saleDate) return false;
        return saleDate >= cutoffDate && saleDate <= today;
      });
    } else {
      filteredSales = [...sales];
    }

    if (filteredSales.length === 0 && sales.length > 0) {
      filteredSales = [...sales];
    }
    return filteredSales;
  };

  const generateCustomerSummaryPDF = (currentDate) => {
    const doc = new jsPDF();
    const periodLabel = getPeriodLabel();

    doc.addImage(Logo, "PNG", 14, 10, 15, 15);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(hotelName, 35, 15);
    doc.setFont("normal");
    doc.text(`Customer Sales Summary - ${periodLabel}`, 35, 23);

    doc.setFontSize(10);
    const fullName = userData?.firstName && userData?.lastName
      ? `${userData.firstName} ${userData.lastName}`
      : user?.displayName || "Unknown";
    doc.text(`Generated by: ${fullName}`, 35, 28);
    doc.text(`Generated on: ${formatDateTime(new Date())}`, 35, 33);

    const filteredSales = getFilteredSales();  
    
    if (filteredSales.length === 0) {
      doc.setFontSize(14);
      doc.text("No sales data available", 35, 60);
      doc.save(`customer_summary_${periodLabel.replace(/ /g, "_")}_${currentDate}.pdf`);
      return;
    }

    // Split into normal and linked
    const normalSales = filteredSales.filter(sale => !sale.isLinked);
    const linkedSales = filteredSales.filter(sale => sale.isLinked === true);

    let finalY = 40;

    // ───────────────────── Normal Sales Table ─────────────────────
    if (normalSales.length > 0) {
      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.text("Normal Sales (Walk-in)", 14, finalY);
      finalY += 2;

      const groupedNormal = {};
      normalSales.forEach((sale, index) => {
        const customerName = (sale.customerName || "").trim();
        const customerPhone = (sale.customerPhone || "").trim();
        let customerKey = customerName && customerPhone 
          ? `${customerName}_${customerPhone}`.toLowerCase().replace(/\s+/g, '_')
          : `ANONYMOUS_${index}`;

        if (!groupedNormal[customerKey]) {
          groupedNormal[customerKey] = {
            customerName: customerName || "Unknown Customer",
            customerPhone: customerPhone || "N/A",
            cashierNames: new Set(),
            dates: new Set(),
            totalCost: 0,
            totalCostPrice: 0,
            totalProfit: 0,
          };
        }

        const customer = groupedNormal[customerKey];
        customer.cashierNames.add(sale.cashierName || "Unknown Cashier");
        const saleDate = convertFirestoreTimestamp(sale.timestamp);
        if (saleDate) customer.dates.add(formatDate(saleDate));
        customer.totalCost += Number(sale.grandTotal) || 0;
        customer.totalCostPrice += Number(sale.totalCost) || 0;
        customer.totalProfit += Number(sale.totalProfit) || 0;
      });

      const tableDataNormal = Object.values(groupedNormal).map((customer, index) => [
        index + 1,
        customer.customerName,
        customer.customerPhone,
        Array.from(customer.cashierNames).join(", "),
        Array.from(customer.dates).join(", "),
        formatAmount(customer.totalCost),
        formatAmount(customer.totalCostPrice),
        formatAmount(customer.totalProfit),
      ]);

      autoTable(doc, {
        startY: finalY,
        head: [["S/N", "Customer Name", "Customer Phone", "Cashier Name", "Date", "Total Cost", "Total Cost Price", "Total Profit"]],
        body: tableDataNormal,
        theme: "striped",
        headStyles: { 
          fillColor: [41, 128, 185],
          textColor: [255, 255, 255],
          fontStyle: 'bold'
        },
        bodyStyles: { textColor: [0, 0, 0] },
        styles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 10 },
          1: { cellWidth: 30 },
          2: { cellWidth: 25 },
          3: { cellWidth: 25 },
          4: { cellWidth: 25 },
          5: { cellWidth: 20 },
          6: { cellWidth: 20 },
          7: { cellWidth: 20 },
        },
        margin: { top: finalY + 2 },
      });

      finalY = doc.lastAutoTable.finalY + 5;

      const normalTotal = normalSales.reduce((sum, sale) => sum + (Number(sale.grandTotal) || 0), 0);
      doc.setFontSize(12);
      doc.text(`Normal Sales Total: ${formatAmount(normalTotal)}`, 14, finalY);
      finalY += 15;
    }

    // ───────────────────── Linked Sales Table ─────────────────────
    if (linkedSales.length > 0) {
      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.text("Linked Sales (Hotel Bookings)", 14, finalY);
      finalY += 2;

      const groupedLinked = {};
      linkedSales.forEach((sale, index) => {
        const customerName = (sale.customerName || "").trim();
        const customerPhone = (sale.customerPhone || "").trim();
        let customerKey = customerName && customerPhone 
          ? `${customerName}_${customerPhone}`.toLowerCase().replace(/\s+/g, '_')
          : `ANONYMOUS_${index}`;

        if (!groupedLinked[customerKey]) {
          groupedLinked[customerKey] = {
            customerName: customerName || "Unknown Guest",
            customerPhone: customerPhone || "N/A",
            cashierNames: new Set(),
            dates: new Set(),
            totalCost: 0,
            totalCostPrice: 0,
            totalProfit: 0,
          };
        }

        const customer = groupedLinked[customerKey];
        customer.cashierNames.add(sale.cashierName || "Unknown Cashier");
        const saleDate = convertFirestoreTimestamp(sale.timestamp);
        if (saleDate) customer.dates.add(formatDate(saleDate));
        customer.totalCost += Number(sale.grandTotal) || 0;
        customer.totalCostPrice += Number(sale.totalCost) || 0;
        customer.totalProfit += Number(sale.totalProfit) || 0;
      });

      const tableDataLinked = Object.values(groupedLinked).map((customer, index) => [
        index + 1,
        customer.customerName,
        customer.customerPhone,
        Array.from(customer.cashierNames).join(", "),
        Array.from(customer.dates).join(", "),
        formatAmount(customer.totalCost),
        formatAmount(customer.totalCostPrice),
        formatAmount(customer.totalProfit),
      ]);

      autoTable(doc, {
        startY: finalY,
        head: [["S/N", "Guest Name", "Phone", "Cashier Name", "Date", "Total Cost", "Total Cost Price", "Total Profit"]],
        body: tableDataLinked,
        theme: "striped",
        headStyles: { 
          fillColor: [41, 128, 185],
          textColor: [255, 255, 255],
          fontStyle: 'bold'
        },
        bodyStyles: { textColor: [0, 0, 0] },
        styles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 10 },
          1: { cellWidth: 30 },
          2: { cellWidth: 25 },
          3: { cellWidth: 25 },
          4: { cellWidth: 25 },
          5: { cellWidth: 20 },
          6: { cellWidth: 20 },
          7: { cellWidth: 20 },
        },
        margin: { top: finalY + 2 },
      });

      finalY = doc.lastAutoTable.finalY + 5;

      const linkedTotal = linkedSales.reduce((sum, sale) => sum + (Number(sale.grandTotal) || 0), 0);
      doc.setFontSize(12);
      doc.text(`Linked Sales Total: ${formatAmount(linkedTotal)}`, 14, finalY);
      finalY += 10;
    }

    // Overall totals
    const totalSales = filteredSales.reduce((sum, sale) => sum + (Number(sale.grandTotal) || 0), 0);
    const totalCost = filteredSales.reduce((sum, sale) => sum + (Number(sale.totalCost) || 0), 0);
    const totalProfit = filteredSales.reduce((sum, sale) => sum + (Number(sale.totalProfit) || 0), 0);

    doc.setFontSize(14);
    doc.text(`Grand Total Sales: ${formatAmount(totalSales)}`, 14, finalY);
    doc.text(`Grand Total Cost: ${formatAmount(totalCost)}`, 14, finalY + 8);
    doc.text(`Grand Total Profit: ${formatAmount(totalProfit)}`, 14, finalY + 16);

    // Footer page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 30, doc.internal.pageSize.height - 10);
    }

    doc.save(`customer_summary_${periodLabel.replace(/ /g, "_")}_${currentDate}.pdf`);
  };

  const generateItemsBreakdownPDF = (currentDate) => {
    const doc = new jsPDF();
    const periodLabel = getPeriodLabel();

    doc.addImage(Logo, "PNG", 14, 10, 15, 15);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(hotelName, 35, 15);
    doc.setFont("normal");
    doc.text(`Sales Items Breakdown - ${periodLabel}`, 35, 23);

    doc.setFontSize(10);
    const fullName = userData?.firstName && userData?.lastName
      ? `${userData.firstName} ${userData.lastName}`
      : user?.displayName || "Unknown";
    doc.text(`Generated by: ${fullName}`, 35, 28);
    doc.text(`Generated on: ${formatDateTime(new Date())}`, 35, 33);

    const filteredSales = getFilteredSales();
    
    if (filteredSales.length === 0) {
      doc.setFontSize(14);
      doc.text("No sales data available", 35, 60);
      doc.save(`sales_breakdown_${periodLabel.replace(/ /g, "_")}_${currentDate}.pdf`);
      return;
    }

    const normalSales = filteredSales.filter(sale => !sale.isLinked);
    const linkedSales = filteredSales.filter(sale => sale.isLinked === true);

    let finalY = 40;

    // ───────────────────── Normal Sales Breakdown ─────────────────────
    if (normalSales.length > 0) {
      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.text("Normal Sales (Walk-in)", 14, finalY);
      finalY += 2;

      const groupedByDateNormal = {};
      normalSales.forEach((sale) => {
        const saleDate = convertFirestoreTimestamp(sale.timestamp);
        const dateKey = saleDate ? formatDate(saleDate) : "Unknown Date";
        if (!groupedByDateNormal[dateKey]) {
          groupedByDateNormal[dateKey] = { date: dateKey, sales: [] };
        }
        groupedByDateNormal[dateKey].sales.push(sale);
      });

      Object.values(groupedByDateNormal).forEach((dateGroup, dateIndex) => {
        finalY += 5;
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text(dateGroup.date, 14, finalY);

        dateGroup.sales.forEach((sale, saleIndex) => {
          finalY += 5;
          doc.setFontSize(10);
          doc.setFont("bold");
          doc.text(
            `${saleIndex + 1}: ${sale.cashierName || "Unknown Cashier"} (${formatDate(convertFirestoreTimestamp(sale.timestamp)) || "N/A"}) - Customer: ${sale.customerName || "Unknown Customer"} [${sale.customerPhone || "N/A"}]`,
            14,
            finalY
          );

          if (!sale.products || sale.products.length === 0) {
            finalY += 5;
            doc.setFontSize(9);
            doc.setFont("normal");
            doc.text("No items in this sale", 20, finalY);
            finalY += 10;
            return;
          }

          const itemsTableData = sale.products.map((product, itemIndex) => [
            itemIndex + 1,
            product.name || "Unknown Product",
            product.quantity?.toString() || "0",
            formatAmount(product.unitPrice || 0),
            formatAmount(product.total || 0),
            formatAmount(product.costPrice || 0),
            formatAmount((product.costPrice || 0) * (product.quantity || 1)),
            formatAmount(product.profit || 0),
          ]);

          finalY += 5;
          autoTable(doc, {
            startY: finalY,
            head: [["S/N", "Product Name", "Quantity", "Unit Price", "Total", "Cost Price", "Total Cost Price", "Profit"]],
            body: itemsTableData,
            theme: "striped",
            headStyles: { 
              fillColor: [100, 100, 100],
              textColor: [255, 255, 255]
            },
            bodyStyles: { textColor: [0, 0, 0] },
            margin: { left: 20, right: 14 },
            styles: { fontSize: 8 },
            columnStyles: {
              0: { cellWidth: 10 },
              1: { cellWidth: 40 },
              2: { cellWidth: 15 },
              3: { cellWidth: 20 },
              4: { cellWidth: 20 },
              5: { cellWidth: 20 },
              6: { cellWidth: 20 },
              7: { cellWidth: 20 },
            },
          });

          finalY = doc.lastAutoTable.finalY + 2;
        });
      });
    }

    // ───────────────────── Linked Sales Breakdown ─────────────────────
    if (linkedSales.length > 0) {
      finalY += 10;
      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.text("Linked Sales (Hotel Bookings)", 14, finalY);
      finalY += 2;

      const groupedByDateLinked = {};
      linkedSales.forEach((sale) => {
        const saleDate = convertFirestoreTimestamp(sale.timestamp);
        const dateKey = saleDate ? formatDate(saleDate) : "Unknown Date";
        if (!groupedByDateLinked[dateKey]) {
          groupedByDateLinked[dateKey] = { date: dateKey, sales: [] };
        }
        groupedByDateLinked[dateKey].sales.push(sale);
      });

      Object.values(groupedByDateLinked).forEach((dateGroup, dateIndex) => {
        finalY += 5;
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text(dateGroup.date, 14, finalY);

        dateGroup.sales.forEach((sale, saleIndex) => {
          finalY += 5;
          doc.setFontSize(10);
          doc.setFont("bold");
          doc.text(
            `${saleIndex + 1}: ${sale.cashierName || "Unknown Cashier"} (${formatDate(convertFirestoreTimestamp(sale.timestamp)) || "N/A"}) - Guest: ${sale.customerName || "Unknown Guest"} [${sale.customerPhone || "N/A"}]`,
            14,
            finalY
          );

          if (!sale.products || sale.products.length === 0) {
            finalY += 5;
            doc.setFontSize(9);
            doc.setFont("normal");
            doc.text("No items in this sale", 20, finalY);
            finalY += 10;
            return;
          }

          const itemsTableData = sale.products.map((product, itemIndex) => [
            itemIndex + 1,
            product.name || "Unknown Product",
            product.quantity?.toString() || "0",
            formatAmount(product.unitPrice || 0),
            formatAmount(product.total || 0),
            formatAmount(product.costPrice || 0),
            formatAmount((product.costPrice || 0) * (product.quantity || 1)),
            formatAmount(product.profit || 0),
          ]);

          finalY += 5;
          autoTable(doc, {
            startY: finalY,
            head: [["S/N", "Product Name", "Quantity", "Unit Price", "Total", "Cost Price", "Total Cost Price", "Profit"]],
            body: itemsTableData,
            theme: "striped",
            headStyles: { 
              fillColor: [100, 100, 100],
              textColor: [255, 255, 255]
            },
            bodyStyles: { textColor: [0, 0, 0] },
            margin: { left: 20, right: 14 },
            styles: { fontSize: 8 },
            columnStyles: {
              0: { cellWidth: 10 },
              1: { cellWidth: 40 },
              2: { cellWidth: 15 },
              3: { cellWidth: 20 },
              4: { cellWidth: 20 },
              5: { cellWidth: 20 },
              6: { cellWidth: 20 },
              7: { cellWidth: 20 },
            },
          });

          finalY = doc.lastAutoTable.finalY + 2;
        });
      });
    }

    const totalSales = filteredSales.reduce((sum, sale) => sum + (Number(sale.grandTotal) || 0), 0);
    const totalCost = filteredSales.reduce((sum, sale) => sum + (Number(sale.totalCost) || 0), 0);
    const totalProfit = filteredSales.reduce((sum, sale) => sum + (Number(sale.totalProfit) || 0), 0);

    finalY += 10;
    doc.setFontSize(14);
    doc.text(`Total Sales: ${formatAmount(totalSales)}`, 14, finalY);
    doc.text(`Total Cost: ${formatAmount(totalCost)}`, 14, finalY + 8);
    doc.text(`Total Profit: ${formatAmount(totalProfit)}`, 14, finalY + 16);

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 30, doc.internal.pageSize.height - 10);
    }

    doc.save(`sales_breakdown_${periodLabel.replace(/ /g, "_")}_${currentDate}.pdf`);
  };

  const generatePDF = () => {
    const currentDate = formatDate(new Date()).replace(/ /g, "_");
    generateCustomerSummaryPDF(currentDate);
    setTimeout(() => {
      generateItemsBreakdownPDF(currentDate);
    }, 1000);
  };

  return (
    <button className="list-btn" onClick={generatePDF}>
      <i className="fas fa-print"></i>
    </button>
  );
};

export default SalesPDFGenerator;
